import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarClockIcon, ChevronLeftIcon, ChevronRightIcon } from './Icons';
import { localDateStr } from './callUtils';

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => i * 5);

// value/onChange use the same 'YYYY-MM-DDTHH:MM' format as the native
// <input type="datetime-local"> this component replaces, so callers don't
// need to change how they read/send the follow-up date.
const splitValue = (value) => {
  if (!value) return { date: null, hour: null, minute: null, period: 'AM' };
  const [datePart, timePart] = value.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour24, minute] = (timePart || '00:00').split(':').map(Number);
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return { date: new Date(year, month - 1, day), hour: hour12, minute, period };
};

const to24Hour = (hour12, period) => {
  if (period === 'AM') return hour12 === 12 ? 0 : hour12;
  return hour12 === 12 ? 12 : hour12 + 12;
};

const buildValue = (date, hour12, minute, period) => {
  if (!date || hour12 == null || minute == null) return '';
  const hour24 = to24Hour(hour12, period);
  return `${localDateStr(date)}T${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const buildMonthCells = (year, month) => {
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array(firstWeekday).fill(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month, day));
  }
  return cells;
};

const formatTriggerLabel = (value) => {
  if (!value) return 'Select date & time';
  const { date, hour, minute, period } = splitValue(value);
  const dateLabel = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${dateLabel}, ${hour}:${String(minute).padStart(2, '0')} ${period}`;
};

// Single date + time popover, styled to match DateRangePicker's trigger/
// calendar look — replaces a native <input type="datetime-local"> so the
// picker renders consistently instead of each browser's own widget.
//
// The popover itself is portaled to document.body (see SelectMenu for the
// same pattern) rather than rendered inline: several callers — e.g. the
// Reschedule follow-up dialog — sit inside an ancestor with overflow-y:
// auto/hidden (the modal body/panel), which would otherwise clip the
// calendar instead of letting it float above the dialog.
function DateTimePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(null);
  const [draftHour, setDraftHour] = useState(9);
  const [draftMinute, setDraftMinute] = useState(0);
  const [draftPeriod, setDraftPeriod] = useState('AM');
  const [viewDate, setViewDate] = useState(new Date());
  const [popoverStyle, setPopoverStyle] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current?.contains(event.target)) return;
      if (popoverRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    // Scroll position (of the page or a scrollable ancestor like the modal
    // body) would leave a fixed-position popover detached from its trigger,
    // so close rather than let it drift. `true` catches scrolls on any
    // inner scroll container, not just the window.
    const handleScroll = () => setOpen(false);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  const handleTriggerClick = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const current = splitValue(value);
    setDraftDate(current.date);
    setDraftHour(current.hour || 9);
    setDraftMinute(current.minute ?? 0);
    setDraftPeriod(current.period);
    setViewDate(current.date || new Date());

    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const popoverWidth = Math.min(window.innerWidth * 0.94, 300);
    // Clamped so a full-width popover still can't run off the right edge.
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - popoverWidth - 8));
    // Placed below for the first paint; the layout effect below corrects
    // top/maxHeight and reveals it once it can measure real content height
    // (see that effect for why `bottom` isn't used for the open-upward case
    // instead of this measure-then-place approach).
    setPopoverStyle({
      position: 'fixed',
      left,
      width: popoverWidth,
      top: rect.bottom + 6,
      visibility: 'hidden',
    });
    setOpen(true);
  };

  // Runs after the popover mounts or its month changes (still before paint,
  // so no visible flash) to measure its real content height and correct the
  // position/visibility set above. Deliberately avoids `bottom`-anchored
  // positioning for the open-upward case: a `position: fixed` box with
  // `bottom` set, `overflow-y: auto`, and no explicit `height` collapses to
  // a sliver in Chromium instead of sizing to its content (the calendar
  // grid renders but outside the box's own claimed bounds, so it's
  // invisible and unclickable) — using `top` everywhere, computed from a
  // real measured height, sidesteps that bug.
  //
  // Measures `scrollHeight`, not `getBoundingClientRect().height`: months
  // have five or six calendar rows depending on how the 1st falls, so the
  // popover's natural content height changes as you page between months
  // while it's open. scrollHeight reports that true, unclamped content
  // height even while a shorter month's maxHeight is still applied from the
  // previous measurement, so re-running this on viewDate change reflows the
  // box to fit rather than reading back its own stale clamp.
  useLayoutEffect(() => {
    if (!open) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    const popoverHeight = popoverRef.current?.scrollHeight;
    if (!rect || !popoverHeight) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < popoverHeight + 12 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(160, (openUp ? spaceAbove : spaceBelow) - 12);
    const top = openUp
      ? Math.max(8, rect.top - 6 - Math.min(popoverHeight, maxHeight))
      : rect.bottom + 6;
    setPopoverStyle((prev) => (prev ? { ...prev, top, maxHeight, overflowY: 'auto', visibility: 'visible' } : prev));
  }, [open, viewDate]);

  const handleApply = () => {
    onChange(buildValue(draftDate, draftHour, draftMinute, draftPeriod));
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const todayStr = localDateStr(new Date());
  const draftDateStr = draftDate ? localDateStr(draftDate) : null;

  return (
    <div className="datetime-picker" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className="date-range-trigger datetime-picker-trigger"
        onClick={handleTriggerClick}
      >
        <CalendarClockIcon width={15} height={15} />
        <span>{formatTriggerLabel(value)}</span>
      </button>
      {open &&
        popoverStyle &&
        createPortal(
        <div ref={popoverRef} className="date-range-popover datetime-picker-popover" style={popoverStyle}>
          <div className="date-range-nav-row">
            <button
              type="button"
              className="date-range-nav"
              onClick={() => setViewDate(new Date(year, month - 1, 1))}
              aria-label="Previous month"
            >
              <ChevronLeftIcon width={16} height={16} />
            </button>
            <div className="datetime-picker-title">{MONTH_LABELS[month]} {year}</div>
            <button
              type="button"
              className="date-range-nav"
              onClick={() => setViewDate(new Date(year, month + 1, 1))}
              aria-label="Next month"
            >
              <ChevronRightIcon width={16} height={16} />
            </button>
          </div>
          <div className="date-range-month">
            <div className="date-range-weekdays">
              {WEEKDAY_LABELS.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
            <div className="date-range-days">
              {buildMonthCells(year, month).map((date, index) => {
                if (!date) return <span key={`blank-${index}`} className="date-range-cell" />;
                const cellValue = localDateStr(date);
                const isSelected = cellValue === draftDateStr;
                return (
                  <span
                    key={cellValue}
                    className={[
                      'date-range-cell',
                      isSelected ? 'range-start range-end' : '',
                      cellValue === todayStr ? 'is-today' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <button type="button" className="date-range-day" onClick={() => setDraftDate(date)}>
                      {date.getDate()}
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
          <div className="datetime-picker-time-row">
            <select
              className="datetime-picker-select"
              value={draftHour}
              onChange={(event) => setDraftHour(Number(event.target.value))}
              aria-label="Hour"
            >
              {HOUR_OPTIONS.map((hour) => (
                <option key={hour} value={hour}>{hour}</option>
              ))}
            </select>
            <span className="datetime-picker-colon">:</span>
            <select
              className="datetime-picker-select"
              value={draftMinute}
              onChange={(event) => setDraftMinute(Number(event.target.value))}
              aria-label="Minute"
            >
              {MINUTE_OPTIONS.map((minute) => (
                <option key={minute} value={minute}>{String(minute).padStart(2, '0')}</option>
              ))}
            </select>
            <div className="datetime-picker-period">
              <button
                type="button"
                className={draftPeriod === 'AM' ? 'active' : ''}
                onClick={() => setDraftPeriod('AM')}
              >
                AM
              </button>
              <button
                type="button"
                className={draftPeriod === 'PM' ? 'active' : ''}
                onClick={() => setDraftPeriod('PM')}
              >
                PM
              </button>
            </div>
          </div>
          <div className="date-range-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClear}>
              Clear
            </button>
            <button type="button" className="btn btn-primary" onClick={handleApply} disabled={!draftDate}>
              Apply
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default DateTimePicker;
